import { ResolutionOptionsStrings } from 'enums/ResolutionOptions'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { ResolutionOptions } from '../../../../../../enums'
import {
  getPerCapitaTooltipVal,
  getPercentShared,
  getPercentShareTooltipVal,
  getRoundedPerCapita
} from './format'
import getLabel from './label'
import { sortByValue, sortByPerCapita } from './sort'

const getColumnNames = (resolution: ResolutionOptionsStrings) => {
  switch (resolution) {
    case ResolutionOptions.COUNTRIES:
    case ResolutionOptions.STATES_PROVINCES:
    case ResolutionOptions.MARKET_AREAS:
      return ['Location', 'LocationName', 'Value', 'TooltipValue']
    case ResolutionOptions.CITIES:
      return ['Latitude', 'Longitude', 'Location', 'Value', 'TooltipValue']
    default:
      throw new Error(`Unsupported Resolution: ${resolution}`)
  }
}

const getDisplayedInsightSet = (
  insightSet: InsightData[],
  resolution: ResolutionOptionsStrings
) => {
  switch (resolution) {
    case ResolutionOptions.COUNTRIES:
    case ResolutionOptions.STATES_PROVINCES:
      // check isoname if we're dealing with countries or states provinces
      return insightSet.filter((s) => !!s.isoName)
    case ResolutionOptions.MARKET_AREAS:
    case ResolutionOptions.CITIES:
      // set max to 100 if we're dealing with cities or market areas
      return insightSet.slice(0, 100)
    default:
      throw new Error(`Unsupported Resolution: ${resolution}`)
  }
}

const getInsightToGeoDataMapper = (
  resolution: ResolutionOptionsStrings,
  isPerCapita: boolean,
  totalInsightVal?: number
): ((insight: InsightData) => (string | number)[]) => {
  let getValue: (insight: InsightData) => number
  let getTooltipValue: (insight: InsightData) => string
  if (isPerCapita) {
    getValue = getRoundedPerCapita
    getTooltipValue = getPerCapitaTooltipVal
  } else {
    getValue = (insight) => getPercentShared(insight, totalInsightVal!)
    getTooltipValue = (insight) => getPercentShareTooltipVal(insight, totalInsightVal!)
  }

  switch (resolution) {
    case ResolutionOptions.COUNTRIES:
    case ResolutionOptions.STATES_PROVINCES:
      return (insight) => [
        insight.isoName!,
        getLabel(insight) || insight.name!,
        getValue(insight),
        getTooltipValue(insight)
      ]
    case ResolutionOptions.MARKET_AREAS:
      return (insight) => [insight.id!, insight.name!, getValue(insight), getTooltipValue(insight)]
    case ResolutionOptions.CITIES:
      return (insight) => [
        insight.latitude!,
        insight.longitude!,
        getLabel(insight) || insight.name!,
        getValue(insight),
        getTooltipValue(insight)
      ]
    default:
      throw new Error(`Unsupported Resolution: ${resolution}`)
  }
}

const createGeoDataFromInsightSet = (
  insightSet: InsightData[],
  resolution: ResolutionOptionsStrings,
  isPerCapita: boolean
) => {
  // for calculating percentage
  const totalInsightVal = isPerCapita
    ? undefined
    : insightSet.reduce((sum, next) => sum + next.value!, 0)

  insightSet.sort(isPerCapita ? sortByPerCapita : sortByValue)

  const columnNames = getColumnNames(resolution)
  const displayedInsightSet = getDisplayedInsightSet(insightSet, resolution)
  const toGeoData = getInsightToGeoDataMapper(resolution, isPerCapita, totalInsightVal)
  const result = [columnNames, ...displayedInsightSet.map(toGeoData)]
  return result
}

export default createGeoDataFromInsightSet
